import { DisplayElement } from '../_types/util.types';

export enum ClaimSortElement {
  ID = 'ID',
  CUSTOMER = 'CUSTOMER',
  TYPE = 'TYPE',
  CATEGORY_NAME = 'CATEGORY_NAME',
  CREATION_DATE = 'CREATION_DATE',
  UPDATE_DATE = 'UPDATE_DATE',
  STATE = 'STATE',
  CUSTOMER_SUPPORT = 'CUSTOMER_SUPPORT',
}

export enum LeadSortElement {
  ID = 'ID',
  CUSTOMER = 'CUSTOMER',
  PARTNER = 'PARTNER',
  TYPE = 'TYPE',
  TASK = 'TASK',
  CREATION_DATE = 'CREATION_DATE',
  UPDATE_DATE = 'UPDATE_DATE',
  STATE = 'STATE',
  CUSTOMER_SUPPORT = 'CUSTOMER_SUPPORT',
  CANCELLATION_INSURANCE_TYPE = 'CANCELLATION_INSURANCE_TYPE',
}

export enum ProposalSortElement {
  ID = 'ID',
  CUSTOMER = 'CUSTOMER',
  PARTNER = 'PARTNER',
  TYPE = 'TYPE',
  TASK = 'TASK',
  CREATION_DATE = 'CREATION_DATE',
  UPDATE_DATE = 'UPDATE_DATE',
  STATE = 'STATE',
  CUSTOMER_SUPPORT = 'CUSTOMER_SUPPORT',
  RENEWAL_DATE = 'RENEWAL_DATE',
}

export enum InsuranceFlowSortElement {
  ID = 'ID',
  CUSTOMER = 'CUSTOMER',
  PARTNER = 'PARTNER',
  TYPE = 'TYPE',
  CATEGORY_NAME = 'CATEGORY_NAME',
  TASK = 'TASK',
  CREATION_DATE = 'CREATION_DATE',
  UPDATE_DATE = 'UPDATE_DATE',
  STATE = 'STATE',
  CUSTOMER_SUPPORT = 'CUSTOMER_SUPPORT',
}

export const getSortElementLabel = (
  value:
    | LeadSortElement
    | ClaimSortElement
    | ProposalSortElement
    | InsuranceFlowSortElement
): string => {
  switch (value) {
    case LeadSortElement.ID:
      return $localize`Id`;
    case LeadSortElement.CUSTOMER:
      return $localize`Client`;
    case LeadSortElement.PARTNER:
      return $localize`Partenaire`;
    case LeadSortElement.TYPE:
      return $localize`Type`;
    case LeadSortElement.TASK:
      return $localize`Affaire`;
    case LeadSortElement.CREATION_DATE:
      return $localize`Date de création`;
    case LeadSortElement.UPDATE_DATE:
      return $localize`Màj`;
    case LeadSortElement.STATE:
      return $localize`Statut`;
    case LeadSortElement.CUSTOMER_SUPPORT:
      return $localize`Chargé de clientèle`;
    case ProposalSortElement.RENEWAL_DATE:
      return $localize`Date de renouvellement suggérée`;
    case LeadSortElement.CANCELLATION_INSURANCE_TYPE:
      return $localize`Assurance à annuler`;
    case InsuranceFlowSortElement.CATEGORY_NAME:
      return $localize`Assureur`;
    default:
      return value as string;
  }
};

export const getSortMethod = (
  value:
    | LeadSortElement
    | ClaimSortElement
    | ProposalSortElement
    | InsuranceFlowSortElement
    | null
): ((element1: DisplayElement, element2: DisplayElement) => number) => {
  switch (value) {
    case LeadSortElement.ID:
      return (x, y) => {
        if (!y.id && !x.id) return 0;
        if (!x.id) return -1;
        if (!y.id) return 1;
        const upperX = x.id?.toLocaleLowerCase();
        const upperY = y.id?.toLocaleLowerCase();
        if (upperX < upperY) {
          return 1;
        }
        if (upperX > upperY) {
          return -1;
        }
        return 0;
      };
    case LeadSortElement.CUSTOMER:
      return (x, y) => {
        if (!y.customerName && !x.customerName) return 0;
        if (!x.customerName) return -1;
        if (!y.customerName) return 1;
        const upperX = x.customerName?.toLocaleLowerCase();
        const upperY = y.customerName?.toLocaleLowerCase();
        if (upperX < upperY) {
          return 1;
        }
        if (upperX > upperY) {
          return -1;
        }
        return 0;
      };
    case LeadSortElement.PARTNER:
      return (x, y) => {
        if (!y.partnerName && !x.partnerName) return 0;
        if (!x.partnerName) return -1;
        if (!y.partnerName) return 1;
        const upperX = x.partnerName?.toLocaleLowerCase();
        const upperY = y.partnerName?.toLocaleLowerCase();
        if (upperX < upperY) {
          return 1;
        }
        if (upperX > upperY) {
          return -1;
        }
        return 0;
      };
    case LeadSortElement.TYPE:
      return (x, y) => {
        if (!y.insuranceType && !x.insuranceType) return 0;
        if (!x.insuranceType) return -1;
        if (!y.insuranceType) return 1;
        const upperX = `${x.insuranceType?.toLocaleLowerCase() ?? ''} ${x.displayedLeadType ?? x.leadType}`;
        const upperY = `${y.insuranceType?.toLocaleLowerCase() ?? ''} ${y.displayedLeadType ?? y.leadType}`;
        if (upperX < upperY) {
          return 1;
        }
        if (upperX > upperY) {
          return -1;
        }
        return 0;
      };
    case LeadSortElement.TASK:
      return (x, y) => {
        if (!y.leadType && !x.leadType) return 0;
        if (!x.leadType) return -1;
        if (!y.leadType) return 1;
        const upperX =
          x.displayedLeadType?.toLocaleLowerCase() ??
          x.leadType?.toLocaleLowerCase();
        const upperY =
          y.displayedLeadType?.toLocaleLowerCase() ??
          y.leadType?.toLocaleLowerCase();
        if (upperX < upperY) {
          return 1;
        }
        if (upperX > upperY) {
          return -1;
        }
        return 0;
      };
    case LeadSortElement.UPDATE_DATE:
      return (x, y) => {
        if (!x.lastUpdateDate && !y.lastUpdateDate) return 0;
        if (!x.lastUpdateDate) return -1;
        if (!y.lastUpdateDate) return 1;
        return (
          new Date(y.lastUpdateDate).getTime() -
          new Date(x.lastUpdateDate).getTime()
        );
      };
    case LeadSortElement.STATE:
      return (x, y) => {
        if (!y.state && !x.state) return 0;
        if (!x.state) return -1;
        if (!y.state) return 1;
        const upperX = x.state?.toLocaleLowerCase();
        const upperY = y.state?.toLocaleLowerCase();
        if (upperX < upperY) {
          return 1;
        }
        if (upperX > upperY) {
          return -1;
        }
        return 0;
      };
    case LeadSortElement.CUSTOMER_SUPPORT:
      return (x, y) => {
        if (!y.customerSupportName && !x.customerSupportName) return 0;
        if (!x.customerSupportName) return -1;
        if (!y.customerSupportName) return 1;
        const upperX = x.customerSupportName?.toLocaleLowerCase();
        const upperY = y.customerSupportName?.toLocaleLowerCase();
        if (upperX < upperY) {
          return 1;
        }
        if (upperX > upperY) {
          return -1;
        }
        return 0;
      };
    case ProposalSortElement.RENEWAL_DATE:
      return (x, y) => {
        if (!x.recommendedRenewalDate && !y.recommendedRenewalDate) return 0;
        if (!x.recommendedRenewalDate) return -1;
        if (!y.recommendedRenewalDate) return 1;
        return (
          new Date(y.recommendedRenewalDate).getTime() -
          new Date(x.recommendedRenewalDate).getTime()
        );
      };
    case InsuranceFlowSortElement.CATEGORY_NAME:
      return (x, y) => {
        if (!y.insuranceCompany && !x.insuranceCompany) return 0;
        if (!x.insuranceCompany) return 1;
        if (!y.insuranceCompany) return -1;
        const upperX = x.insuranceCompany?.toLocaleLowerCase();
        const upperY = y.insuranceCompany?.toLocaleLowerCase();
        if (upperX < upperY) {
          return 1;
        }
        if (upperX > upperY) {
          return -1;
        }
        return 0;
      };
    case LeadSortElement.CREATION_DATE:
    default:
      return (x, y) =>
        new Date(y.creationDate).getTime() - new Date(x.creationDate).getTime();
  }
};
